import "./mindmap.scss";

import _ from "lodash";
import PropTypes from "prop-types";
import React, { PureComponent } from "react";
import ImmutablePropTypes from "react-immutable-proptypes";
import { connect } from "react-redux";

import { withContainerContext } from "../../components/container/ContainerContext";
import { withScreenContext } from "../../components/screen/ScreenContext";
import { DragDropTypes } from "../../constants/dragDropTypes";
import interfaceMessages from "../../constants/interfaceMessages";
import { appStateOperations, appStateSelectors } from "../../modules/appState";
import batchOperations from "../../modules/batchOperations";
import { batchActions } from "../../modules/chainReducer";
import { definitionSelectors } from "../../modules/definition";
import { colorSelectors } from "../../modules/definition/colors";
import { propertySelectors } from "../../modules/definition/properties";
import { referenceSelectors } from "../../modules/definition/references";
import { typeSelectors } from "../../modules/definition/types";
import { itemOperations, itemSelectors } from "../../modules/items";
import mindmapReducer, { mindmapOperations, mindmapSelectors } from "../../modules/mindmaps";
import { relationshipOperations, relationshipSelectors } from "../../modules/relationships";
import { uiStateOperations, uiStateSelectors } from "../../modules/uiState";
import { shouldAutoEditItems } from "../../modules/undoRedo";
import { immutableRecords, PropTypeShapes } from "../../types";
import { mergeDispatchToProps } from "../../utils/codeSplit";
import hasAncestorOfClass from "../../utils/hasAncestorOfClass";
import scaleRect from "../../utils/scaleRect";
// import debugUtils from "../../utils/debugUtils";
import { trackEvent } from "../../utils/trackAnalytics";
import { appSelectors } from "../app";
import MindmapCanvasView from "./MindmapCanvasView";
import selectors from "./selectors";

class MindmapView extends PureComponent {
  constructor(props, context) {
    super(props, context);

    this.state = {
      displayData: immutableRecords.MindmapDisplayRecord({})
    };
    this._lastAction = null;
    this.dispatchLocal = this.dispatchLocal.bind(this);
    this.internalMoveEvent = this.internalMoveEvent.bind(this);
    this.internalDragLeave = this.internalDragLeave.bind(this);
    this.dragPreviewDataForItemId = this.dragPreviewDataForItemId.bind(this);
    this.onItemClick = this.onItemClick.bind(this);
    this.onItemDoubleClick = this.onItemDoubleClick.bind(this);
    this.getConnectorDropOptions = this.getConnectorDropOptions.bind(this);
    this.requestViewBoundsForZoom = this.requestViewBoundsForZoom.bind(this);
    this.requestCanDropItems = this.requestCanDropItems.bind(this);
    this.onEventDrop = this.onEventDrop.bind(this);
    this.onCanvasClick = this.onCanvasClick.bind(this);
  }

  componentWillMount() {
    this.copyLocalStore(this.props);
  }

  componentWillReceiveProps(nextProps) {
    // debugUtils.logPropChanges("MindmapView", this.props, nextProps, true);
    this.copyLocalStore(nextProps);
  }

  copyLocalStore(props) {
    const displayData = selectors.getMindmapViewData(props.localStore, this.props.viewId);
    this.setState({
      displayData,
      totalRect: displayData.rect
    });
  }

  dispatchLocal(action) {
    // Dispatch local needs to apply to the original state each time, so we always apply it to props.localStore instead
    const newMindmap = mindmapReducer(this.props.localStore.data.mindmaps, action, this.props.localStore);
    const newStore = this.props.localStore.setIn(["data", "mindmaps"], newMindmap);
    const displayData = selectors.getMindmapViewData(newStore, this.props.viewId);

    this.setState({
      displayData
    });
  }

  internalDragLeave() {
    this.copyLocalStore(this.props);
  }

  internalMoveEvent(sourceView, mindmapId, nodeIds, itemIds, deltaX, deltaY, x, y) {
    let action = null;
    if (sourceView === this.props.viewId) {
      action = mindmapOperations.moveNodes(mindmapId, nodeIds, deltaX, deltaY);
    } else {
      action = mindmapOperations.importNodes(mindmapId, itemIds, x, y);
    }
    if (_.isEqual(action, this._lastAction)) {
      return;
    }

    this.dispatchLocal(action, []);
    this._lastAction = action;
  }

  requestViewBoundsForZoom(zoom) {
    let scaledRect = scaleRect(this.state.totalRect, zoom);
    if (scaledRect.width < this.props.containerRect.width) {
      scaledRect = scaledRect.merge({
        width: this.props.containerRect.width,
        right: scaledRect.left + this.props.containerRect.width
      });
    }

    if (scaledRect.height < this.props.containerRect.height) {
      scaledRect = scaledRect.merge({
        height: this.props.containerRect.height,
        bottom: scaledRect.top + this.props.containerRect.height
      });
    }
    return scaledRect;
  }

  dragPreviewDataForItemId(itemId) {
    const previewData = selectors.getMindmapViewDataForItem(this.props.localStore, itemId);
    return previewData;
  }

  requestCanDropItems(dragItem, sourceViewId) {
    const itemIds = dragItem.multipleItemIds;
    const viewId = dragItem.sourceView;
    if (dragItem.itemType === DragDropTypes.TABLE_COLUMN) {
      return {
        canDrop: false,
        message: interfaceMessages.DRAG_DROP_MINDMAP_NOT_ALLOWED
      };
    } else if (viewId != sourceViewId) {
      const existingNodeIds = this.props.localStore.data.mindmaps.byId.get(this.props.mindmapId).nodes.byId;
      if (existingNodeIds.find(item => itemIds.includes(item.itemId))) {
        return {
          canDrop: false,
          message: interfaceMessages.DRAG_DROP_MINDMAP_ALREADY_EXISTS
        };
      }
    }
    return { canDrop: true };
  }

  onItemClick(itemId, modifiers, wasSelected) {
    const processSelection = condition => {
      if (condition) {
        if (wasSelected) {
          this.props.onRemoveFromSelection(itemId);
        } else {
          this.props.onAddToSelection(itemId);
        }
      } else if (!wasSelected) {
        this.props.onSelectItems(itemId);
      }
    };
    //#!if iOS === false
    processSelection(modifiers.CONTROL || modifiers.COMMAND || modifiers.SHIFT);
    //#!elseif iOS === true
// processSelection(this.props.screenContext.isEditMode);
    //#!endif
  }

  onItemDoubleClick() {
    if (this.props.onShowInspector) {
      this.props.onShowInspector();
    }
  }

  onCanvasClick(e) {
    const clickWasNode = hasAncestorOfClass(e.target, "mindmapNode");
    if (!clickWasNode && this.props.selectedItemIds.size > 0) {
      this.props.onClick();
    }
  }

  onEventDrop(sourceView, mindmapId, nodeIds, itemIds, deltaX, deltaY, x, y) {
    this.props.onEventDrop(this.props.viewId, sourceView, mindmapId, nodeIds, itemIds, deltaX, deltaY, x, y);
  }

  getConnectorDropOptions(sourceNode, destNode) {
    const currentRels = relationshipSelectors.getRelationshipsBetweenItems(
      this.props.localStore,
      sourceNode.itemId,
      destNode.itemId
    );
    const allowedRefs = relationshipSelectors.getAllAllowedReferencesBetweenItems(
      this.props.localStore,
      sourceNode.itemId,
      destNode.itemId
    );
    const allowedInverseRefs = relationshipSelectors.getAllAllowedReferencesBetweenItems(
      this.props.localStore,
      destNode.itemId,
      sourceNode.itemId
    );

    // eliminate any bi-directional references that are found going the other way...
    const uniqueInverseRefs = allowedInverseRefs.filter(
      inv => !inv.bidirectional && !allowedRefs.find(ref => ref.id === inv.id)
    );

    // const visibleRefs = allowedRefs.filter(ref => !this.props.hiddenReferenceTypes.contains(ref.id));
    // const visibleInverseRefs = allowedInverseRefs.filter(ref => !this.props.hiddenReferenceTypes.contains(ref.id));

    return {
      relationships: currentRels,
      references: allowedRefs,
      inverseReferences: uniqueInverseRefs
    };
  }

  render() {
    const displayData = this.state.displayData;
    const initialScrollLeft = displayData.nodes.size ? null : this.props.containerRect.width * -0.5;
    const initialScrollTop = displayData.nodes.size ? null : this.props.containerRect.height * -0.5;

    return (
      <div className="mindmapView" onClick={this.onCanvasClick}>
        <MindmapCanvasView
          data={displayData}
          sortedItemIds={this.props.sortedItemIds}
          selectedItemIds={this.props.selectedItemIds}
          zoom={this.props.zoom}
          fontSize={this.props.fontSize}
          mindmapViewSettings={this.props.mindmapViewSettings}
          mindmapId={this.props.mindmapId}
          viewId={this.props.viewId}
          projectId={this.props.projectId}
          isActiveView={this.props.isActiveView}
          defaultType={this.props.defaultType}
          typesById={this.props.typesById}
          propertiesById={this.props.propertiesById}
          colorsById={this.props.colorsById}
          referencesById={this.props.referencesById}
          itemsById={this.props.itemsById}
          onInternalMoveEvent={this.internalMoveEvent}
          onInternalDragLeave={this.internalDragLeave}
          onEventDrop={this.onEventDrop}
          onCreateItem={this.props.onCreateItem}
          onItemClick={this.onItemClick}
          onItemDoubleClick={this.onItemDoubleClick}
          onSelectItems={this.props.onSelectItems}
          onLabelChange={this.props.onLabelChange}
          getConnectorDropOptions={this.getConnectorDropOptions}
          onRelationshipChange={this.props.onRelationshipChange}
          requestDragPreviewDataForItem={this.dragPreviewDataForItemId}
          requestViewBoundsForZoom={this.requestViewBoundsForZoom}
          orderedTypes={this.props.orderedTypes}
          autoEditBlankNodes={this.props.autoEditBlankNodes}
          requestCanDropItems={this.requestCanDropItems}
          dispatchBatchActions={this.props.dispatchBatchActions}
          initialScrollLeft={initialScrollLeft}
          initialScrollTop={initialScrollTop}
        />
      </div>
    );
  }
}

const mapStateToProps = (state, props) => {
  const mindmapId = appStateSelectors.getViewMindmapId(state.present, props.viewId);
  return {
    localStore: state.present,
    sortedItemIds: selectors.getMindmapViewSortedItemIds(state.present, props.viewId),
    selectedItemIds: uiStateSelectors.getSelectedItems(state.present),
    mindmapViewSettings: definitionSelectors.getMindmapViewSettings(state.present),
    hiddenReferenceTypes: mindmapSelectors.getMindmapHiddenReferenceTypes(state.present, mindmapId),
    defaultType: selectors.getMindmapViewDefaultType(state.present),
    mindmapId,
    zoom: appStateSelectors.getViewMindmapZoom(state.present, props.viewId),
    fontSize: appStateSelectors.getFontSize(state.present),
    projectId: appSelectors.getProjectId(state.present),
    isActiveView: appStateSelectors.isViewActive(state.present, props.viewId),
    orderedTypes: typeSelectors.getOrderedNoNarrativeOnlyTypes(state.present),
    typesById: typeSelectors.getTypesById(state.present),
    propertiesById: propertySelectors.getPropertiesById(state.present),
    colorsById: colorSelectors.getColorsById(state.present),
    referencesById: referenceSelectors.getReferencesById(state.present),
    itemsById: itemSelectors.getItemsById(state.present),
    // Disable auto-edit when applying a REDO action
    autoEditBlankNodes: shouldAutoEditItems(state)
  };
};

const readonlyActions = {
  onSelectItems: appStateOperations.selectItems,
  onAddToSelection: appStateOperations.addItemsToSelection,
  onRemoveFromSelection: appStateOperations.removeItemsFromSelection,
  onClick: appStateOperations.clearSelection,
  onShowInspector: uiStateOperations.showInspectorModal
};

const editingActions = {
  onCreateItem: (itemType, mindmapId, x, y) => {
    trackEvent("createItemFromMindmapView", "createItem", itemType);
    return batchOperations.createItemInMindmap(itemType, mindmapId, x, y);
  },
  onLabelChange: itemOperations.editLabel,
  onEventDrop: (viewId, sourceView, mindmapId, nodeIds, itemIds, deltaX, deltaY, x, y) => {
    let actions = [];
    if (sourceView === viewId) {
      actions.push(mindmapOperations.moveNodes(mindmapId, nodeIds, deltaX, deltaY));
    } else {
      actions.push(mindmapOperations.importNodes(mindmapId, itemIds, x, y));
    }
    //#!if iOS !== true
    actions.push(appStateOperations.selectItems(itemIds));
    //#!endif
    return batchActions(actions);
  },
  onRelationshipChange: (subjectId, referenceId, itemId, isChecked) => {
    trackEvent("setItemRelationshipFromMindmapView", "setItemRelationship", referenceId);
    if (isChecked) {
      return relationshipOperations.createRelationship([subjectId], referenceId, itemId);
    } else {
      return relationshipOperations.deleteRelationshipsWithValues(subjectId, referenceId, itemId);
    }
  },
  dispatchBatchActions: actions => {
    return batchOperations.reBatchActions(actions);
  },
  onShowInspector: appStateOperations.showRightPanel
};

const mapDispatchToProps = dispatch => mergeDispatchToProps(readonlyActions, editingActions, dispatch);

MindmapView.propTypes = {
  localStore: PropTypes.object.isRequired,
  viewId: PropTypes.string.isRequired,
  projectId: PropTypes.string.isRequired,
  isActiveView: PropTypes.bool.isRequired,
  mindmapId: PropTypes.string.isRequired,
  zoom: PropTypes.number.isRequired,
  fontSize: PropTypes.number.isRequired,
  onEventDrop: PropTypes.func.isRequired,
  onCreateItem: PropTypes.func.isRequired,
  onLabelChange: PropTypes.func,
  defaultType: PropTypes.string.isRequired,
  mindmapViewSettings: PropTypeShapes.mindmapViewSettings.isRequired,
  hiddenReferenceTypes: ImmutablePropTypes.listOf(PropTypes.string).isRequired,
  sortedItemIds: ImmutablePropTypes.listOf(PropTypes.string).isRequired,
  selectedItemIds: ImmutablePropTypes.listOf(PropTypes.string).isRequired,
  onSelectItems: PropTypes.func.isRequired,
  onAddToSelection: PropTypes.func.isRequired,
  onRemoveFromSelection: PropTypes.func.isRequired,
  onClick: PropTypes.func.isRequired,
  onRelationshipChange: PropTypes.func.isRequired,
  autoEditBlankNodes: PropTypes.bool.isRequired,
  orderedTypes: ImmutablePropTypes.list.isRequired,

  // copy paste
  dispatchBatchActions: PropTypes.func,
  typesById: ImmutablePropTypes.map,
  propertiesById: ImmutablePropTypes.map,
  colorsById: ImmutablePropTypes.map,
  referencesById: ImmutablePropTypes.map,
  itemsById: ImmutablePropTypes.map,

  onShowInspector: PropTypes.func,

  // From containerContext
  containerRect: PropTypeShapes.flatRect.isRequired,

  //#!if iOS === true
  // From withScreenContext
// screenContext: PropTypes.object
  //#!endif
};

const normalView = withContainerContext(MindmapView);
let exportedView = normalView;
//#!if iOS === true
// exportedView = withScreenContext(normalView);
//#!endif

export default connect(mapStateToProps, mapDispatchToProps)(exportedView);
